import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import React from "react";
import { Link } from "react-router-dom";
import { TripsReportsRow } from "../../../api/models/TripsReportsRow";
import { SelectedTrip, Shape } from "../../../interfaces";
import {
    selectTrip,
    setCanUseMapNeighbors,
} from "../../../services/common/common";
import { secToMinSec } from "../../../services/common/convert";

import {
    convertShiftDateToJpn,
    convertUTCtoJST,
    toInlineBatsuMark,
} from "../../../utils/formatting";

import MapIcon from "@mui/icons-material/Map";
import { HeadCell } from "../../../components/table/sortableTable";
import {
    ActionType,
    ResourceType,
    StatusType,
    sendLog,
} from "../../../services/common/send_log";
import { colors, fontSizes } from "../../../theme";

export const headCells: HeadCell[] = [
    {
        label: "要指導",
        id: "need_guidance",
        grow: 1,
        numeric: false,
        narrowRow: true,
    },
    {
        label: "空車開始時刻",
        id: "unladen_start_time",
        numeric: true,
        align: "right",
        headerAlignRight: true,
        grow: 1,
        format: (x: string): string => {
            return convertUTCtoJST(x);
        },
        narrowRow: true,
    },
    {
        label: "空車開始地点",
        id: "unladen_start_city",
        grow: 2,
        numeric: false,
        narrowRow: true,
    },
    {
        label: "乗車時刻",
        id: "deliver_start_time",
        numeric: true,
        align: "right",
        headerAlignRight: true,
        grow: 1,
        wrap: true,
        format: (x: string): string => {
            return convertUTCtoJST(x);
        },
        narrowRow: true,
    },
    {
        label: "乗車地点",
        id: "deliver_start_city",
        numeric: false,
        grow: 1,
        wrap: true,
        narrowRow: true,
    },
    {
        label: "空車時間",
        id: "unladen_time",
        align: "right",
        numeric: true,
        headerAlignRight: true,
        grow: 1,
        wrap: true,
        format: (x: number): string => {
            return secToMinSec(x);
        },
        narrowRow: true,
    },
    {
        label: "走行時間",
        id: "wheel_time",
        align: "right",
        numeric: true,
        grow: 1,
        wrap: true,
        headerAlignRight: true,
        format: (x: number): string => {
            return secToMinSec(x);
        },
        narrowRow: true,
    },
    {
        label: "配車停止時間",
        id: "pause_car_request_sec",
        align: "right",
        numeric: true,
        grow: 1,
        wrap: true,
        headerAlignRight: true,
        format: (x: number): string => {
            return secToMinSec(x);
        },
        narrowRow: true,
    },
    {
        label: "乗車種別",
        id: "boarding_type",
        numeric: false,
        grow: 1,
        wrap: true,
        narrowRow: true,
    },
    {
        label: "ナビ非利用時間",
        id: "navi_off_time",
        align: "right",
        numeric: true,
        headerAlignRight: true,
        grow: 1,
        format: (x: number, row: Row): JSX.Element => {
            return toInlineBatsuMark(secToMinSec(x), row.is_hermes_off);
        },
        narrowRow: true,
    },
    {
        label: "離脱回数",
        id: "deviation_count",
        align: "right",
        headerAlignRight: true,
        numeric: true,
        grow: 1,
        format: (x: number, row: Row): JSX.Element => {
            return toInlineBatsuMark(x, row.is_deviation);
        },
        narrowRow: true,
    },
    {
        label: "GOキャンセル数",
        id: "go_cancel_count",
        numeric: true,
        align: "right",
        headerAlignRight: true,
        grow: 1,
        wrap: true,
        format: (x: number, row: Row): JSX.Element => {
            return toInlineBatsuMark(x, row.is_go_cancel);
        },
        narrowRow: true,
    },
    {
        label: "降車時刻",
        id: "deboarding_time",
        numeric: true,
        headerAlignRight: true,
        align: "right",
        grow: 1,
        wrap: true,
        format: (x: string): string => {
            const cx = x == " " ? " " : convertUTCtoJST(x);
            return cx;
        },
        narrowRow: true,
    },
    {
        label: "降車地",
        id: "deboarding_city",
        numeric: false,
        grow: 1,
        wrap: true,
        narrowRow: true,
    },
    {
        label: "地図",
        id: "trip_session_id",
        cell: function UE(r: Row): JSX.Element {
            return <LinkToMap selectedTrip={r} />;
        },
        headerAlignRight: false,
        numeric: false,
        grow: 2,
        narrowRow: true,
    },
];

export type Row = {
    need_guidance?: string;
    unladen_start_time?: string;
    unladen_start_city?: string;
    deliver_start_time?: string;
    deliver_start_city?: string;
    unladen_time?: number;
    wheel_time?: number;
    pause_car_request_sec?: number;
    boarding_type?: string;
    navi_off_time?: number;
    deviation_count?: number;
    go_cancel_count?: number;
    deboarding_time?: string;
    deboarding_city?: string;
    is_hermes_off?: number;
    navi_off_sec?: number;
    is_deviation?: number;
    is_go_cancel?: number;
    trip_session_id?: string;
    login_session_id?: string;
    trip?: TripsReportsRow;
};

interface MapLinkProps {
    selectedTrip: Row;
}

const LinkToMap: React.FC<MapLinkProps> = (props: MapLinkProps) => {
    let tripShapeExists = false;
    if (props.selectedTrip.trip && props.selectedTrip.trip.unladenShape) {
        tripShapeExists = true;
    }

    let linkColor = colors.gray.main;
    let cursorType = "default";

    if (tripShapeExists) {
        linkColor = colors.green.maplink;
        cursorType = "pointer";
    }

    return (
        <Box style={{ cursor: cursorType }}>
            <MenuItem
                to="/trips_map"
                component={Link}
                sx={{ color: colors.blue.main, p: 0 }}
                onClick={(ev: any) => {
                    if (!tripShapeExists) {
                        ev.preventDefault();
                    }
                }}
            >
                <MapIcon
                    sx={{
                        color: linkColor,
                        fontSize: fontSizes.mapicon,
                        pt: 1,
                        pb: 1,
                    }}
                    onClick={async (ev: any) => {
                        if (!tripShapeExists) {
                            ev.preventDefault();
                            setCanUseMapNeighbors(true);
                            selectTrip(null);
                            return;
                        }

                        const _trip = convertRecToTrip(props.selectedTrip.trip);
                        await selectTrip(_trip);

                        sendLog(
                            ResourceType.TRIPS_MAP,
                            StatusType.GET_RESOURCE,
                            ActionType.TRIPS_MAP_MONITORING
                        );
                    }}
                />
            </MenuItem>
        </Box>
    );
};

const convertRecToTrip = (
    rec?: TripsReportsRow | null
): SelectedTrip | null => {
    if (!rec) {
        return null;
    }

    const shiftDate = rec.shiftDate
        ? convertShiftDateToJpn(rec.shiftDate.toString())
        : "";

    return {
        tripSessionId: rec.tripSessionId || null,
        loginSessionId: rec.loginSessionId || null,
        shiftDate: shiftDate,
        boardingTs: convertUTCtoJST(rec.boardingTs) || null,
        driverCode: rec.driverCode || null,
        driverName: rec.driverName || null,
        unladenStartTs: convertUTCtoJST(rec.unladenStartTs) || null,
        unladenStartTsRaw: rec.unladenStartTs || null,
        boardingType: rec.boardingType || null,
        details: {
            deliverStartLoc: rec.deliverStartLoc as Shape,
            unladenStartLoc: rec.unladenStartLoc as Shape,
            naviShape: rec.naviShape as Shape,
            unladenShape: rec.unladenShape as Shape,
        },
    } as SelectedTrip;
};
